/**
 * MemberFieldGroupsSection
 *
 * Renders all field groups defined for the current org as a section on the
 * member profile. Each group lives in its own react-hook-form FormProvider so
 * the existing RepeatableGroupRenderer (which uses useFieldArray) plugs in
 * directly. "Save" persists the array via PUT /api/members/{id}/field-groups/{group_id}.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Save, Loader2, CheckCircle2, AlertCircle, List, LayoutGrid, ChevronRight, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import useMemberFieldGroups from '@/hooks/useMemberFieldGroups';
import { RepeatableGroupRenderer } from '@/components/forms/RepeatableGroupRenderer';
import type { MemberFieldGroup } from '@/services/api/memberFieldGroups';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface MemberFieldGroupsSectionProps {
  memberId: number;
}

export const MemberFieldGroupsSection: React.FC<MemberFieldGroupsSectionProps> = ({ memberId }) => {
  const { t } = useAppTranslation();
  const { groups, loading, error, saveGroup, refetch } = useMemberFieldGroups(memberId);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          <p className="text-xs mt-2">{t('members.loadingFieldGroups', 'Loading field groups…')}</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-red-600 bg-red-50">
          <AlertCircle className="w-4 h-4 inline mr-2" /> {error}
        </CardContent>
      </Card>
    );
  }

  if (!groups.length) {
    // No groups configured for this org — render nothing rather than an empty card
    return null;
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <MemberFieldGroupCard
          key={group.field_group_id}
          group={group}
          onSave={async (instances) => {
            const result = await saveGroup(group.field_group_id, instances);
            return !!result;
          }}
          onAfterSave={refetch}
        />
      ))}
    </div>
  );
};

// ============================================================================

interface CardProps {
  group: MemberFieldGroup;
  onSave: (instances: Record<string, any>[]) => Promise<boolean>;
  onAfterSave: () => void;
}

const MemberFieldGroupCard: React.FC<CardProps> = ({ group, onSave, onAfterSave }) => {
  const { t } = useAppTranslation();
  // Each group gets its own RHF context — the renderer reads/writes via
  // useFieldArray under `group.field_group_name`.
  const form = useForm<Record<string, any>>({
    defaultValues: { [group.field_group_name]: group.instances.map((i) => i.values) },
    shouldUnregister: false,
  });

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Collapse the whole group card to reclaim vertical space. Persisted per
  // group; empty groups default to collapsed so the profile isn't padded out
  // with unfilled sections.
  const collapseKey = `member_field_group_collapsed_${group.field_group_id}`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(collapseKey);
      if (stored === '0') return false;
      if (stored === '1') return true;
    } catch {}
    return group.instances.length === 0;
  });
  useEffect(() => {
    try { localStorage.setItem(collapseKey, collapsed ? '1' : '0'); } catch {}
  }, [collapsed, collapseKey]);
  // Compact (one-row-per-instance) vs expanded (full form per instance).
  // Defaults to compact for repeatables with ≥2 instances — case managers
  // reviewing a long Goals/Actions list want the table-first view. Persisted
  // per group via localStorage so the choice survives reloads.
  const lsKey = `member_field_group_compact_${group.field_group_id}`;
  const [compactMode, setCompactMode] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(lsKey);
      if (stored === '0') return false;
      if (stored === '1') return true;
    } catch {}
    return group.is_repeatable && group.instances.length >= 2;
  });
  useEffect(() => {
    try { localStorage.setItem(lsKey, compactMode ? '1' : '0'); } catch {}
  }, [compactMode, lsKey]);

  // Reset form whenever the underlying group instances change (e.g. after refetch)
  useEffect(() => {
    form.reset({ [group.field_group_name]: group.instances.map((i) => i.values) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.instances]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const values = form.getValues();
      const arr = values[group.field_group_name] || [];
      const ok = await onSave(arr);
      if (ok) {
        setSavedAt(Date.now());
        onAfterSave();
      } else {
        setSaveError(t('members.failedToSave', 'Failed to save'));
      }
    } catch (e: any) {
      setSaveError(e?.message || t('members.failedToSave', 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2 min-w-0 text-left"
          aria-expanded={!collapsed}
        >
          {collapsed
            ? <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
            : <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />}
          <CardTitle className="text-base flex items-center gap-2">
            {group.field_group_friendly_name}
            {group.is_repeatable ? (
              <Badge variant="default" className="text-xs">
                {t('members.repeatable', 'Repeatable')} · {group.instances.length}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">{t('members.single', 'Single')}</Badge>
            )}
          </CardTitle>
        </button>
        <div className="flex items-center gap-2">
          {/* View toggle: only meaningful when the group is repeatable. */}
          {!collapsed && group.is_repeatable && (
            <button
              type="button"
              onClick={() => setCompactMode((v) => !v)}
              className="text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 border border-gray-200 dark:border-gray-700 rounded px-2 py-1"
              title={compactMode ? t('members.switchToFullView', 'Switch to full view') : t('members.switchToCompactView', 'Switch to compact view')}
            >
              {compactMode ? <LayoutGrid className="w-3 h-3" /> : <List className="w-3 h-3" />}
              <span>{compactMode ? t('members.compact', 'Compact') : t('members.full', 'Full')}</span>
            </button>
          )}
          {!collapsed && savedAt && Date.now() - savedAt < 3000 && (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> {t('members.saved', 'Saved')}
            </span>
          )}
          {!collapsed && (
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {t('members.save', 'Save')}
            </Button>
          )}
        </div>
      </CardHeader>
      {!collapsed && (
      <CardContent>
        <Separator className="mb-4" />
        <FormProvider {...form}>
          <RepeatableGroupRenderer
            field={{
              field_id: group.field_group_name,
              field_type: 'field_group' as any,
              label: group.field_group_friendly_name,
              required: false,
              order: 0,
              field_group_id: group.field_group_id,
            } as any}
            compactMode={compactMode}
          />
        </FormProvider>
        {saveError && (
          <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {saveError}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
};

export default MemberFieldGroupsSection;
